// Query params the web app is allowed to keep in the address bar and send to analytics/Sentry.
// Anything not listed here is stripped at boot and from every payload.
// Hardcoded rather than imported from the SDK so this stays cheap to load before hydration.

interface Env {
  isProduction: boolean;
}

export const APP_PARAMS: readonly string[] = [
  "category",
  "modal",
  "view_as",
  "address",
  "market",
  // Shareable markets-heatmap side; see use-page-url.ts SHAREABLE_PARAMS.
  "movers",
  // Ideas: a shared idea link, and the X account-link result.
  "idea",
  "x",
  // QUERY_PARAM_KEYS from @decibeltrade/sdk
  "sort_key",
  "sort_dir",
  "limit",
  "offset",
  "search_term",
];

// Test-state overrides read by e2e; never allowed in production.
export const NON_PROD_PARAMS: readonly string[] = [
  "cv_state",
  "fftState",
  "funState",
  "agentState",
  "agentLiveTest",
  "agentScrollProbe",
];

export const MARKETING_PARAMS: readonly string[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "twclid",
  "ttclid",
  "li_fat_id",
  "msclkid",
  "rdt_cid",
  "ref",
  "r",
  "_gl",
];

const ALWAYS_ALLOWED = new Set([...APP_PARAMS, ...MARKETING_PARAMS]);
const NON_PROD_ALLOWED = new Set(NON_PROD_PARAMS);

export function isAllowedParam(key: string, { isProduction }: Env): boolean {
  return ALWAYS_ALLOWED.has(key) || (!isProduction && NON_PROD_ALLOWED.has(key));
}

export function sanitizeSearch(search: string, env: Env): string {
  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();
  let dropped = false;
  for (const [key, value] of params) {
    if (isAllowedParam(key, env)) kept.append(key, value);
    else dropped = true;
  }
  if (!dropped) {
    // Preserve the original encoding when nothing changes.
    const raw = search.startsWith("?") ? search.slice(1) : search;
    return raw ? `?${raw}` : "";
  }
  const out = kept.toString();
  return out ? `?${out}` : "";
}

export function sanitizeParams(
  params: URLSearchParams | Record<string, string>,
  env: Env,
): Record<string, string> {
  const entries =
    params instanceof URLSearchParams ? [...params.entries()] : Object.entries(params);
  return Object.fromEntries(entries.filter(([key]) => isAllowedParam(key, env)));
}

const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:\/\//i;
const RELATIVE_URL = /^[/?]/;
const PLACEHOLDER_ORIGIN = "http://placeholder.invalid";

// Drops non-allowlisted params and the hash. Strings that are not URLs come back unchanged.
export function sanitizeUrl(input: string, env: Env): string {
  const absolute = ABSOLUTE_URL.test(input);
  if (!absolute && !RELATIVE_URL.test(input)) return input;
  let url: URL;
  try {
    url = new URL(input, PLACEHOLDER_ORIGIN);
  } catch {
    return input;
  }
  const path = `${url.pathname}${sanitizeSearch(url.search, env)}`;
  if (!absolute) return path;
  // Keep non-hierarchical URLs (e.g. data:, blob:) as-is apart from query/hash removal.
  return url.origin === "null" ? `${url.protocol}${path}` : `${url.origin}${path}`;
}

const SENSITIVE_FRAGMENT_PARAMS: Record<string, true> = {
  id_token: true,
  access_token: true,
};

export function hasSensitiveFragment(hash: string): boolean {
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  return [...new URLSearchParams(fragment).keys()].some(
    (key) => SENSITIVE_FRAGMENT_PARAMS[key.toLowerCase()] === true,
  );
}

// Keeps path, hash and history state.
export function sanitizeAddressBar(env: Env): void {
  const { pathname, search, hash } = window.location;
  const clean = sanitizeSearch(search, env);
  if (clean === search) return;
  window.history.replaceState(window.history.state, "", `${pathname}${clean}${hash}`);
}
